import React, { useState, useEffect, useRef } from "react";
import { Button } from "@material-tailwind/react";
import axiosInstance from "../services/api/axios";
import { toast } from "react-toastify";
import { FaFilePdf } from "react-icons/fa";
import { Spinner } from "@material-tailwind/react";
import { Download, UploadCloud, FileText, X } from "lucide-react";
import { PrimaryButton, SecondaryButton } from "./Buttons";

const OccupancyLetterUpload = ({ projectId , phaseId}) => {
    const [occupancyLetterFile, setOccupancyLetterFile] = useState(null);
    const [occupancyLetters, setOccupancyLetters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const fileInputRef = useRef();

    const handleOccupancyLetterUpload = (event) => {
        event.stopPropagation();
        const file = event.target.files[0];
        if (file) {
            setOccupancyLetterFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.type === 'application/pdf') {
                setOccupancyLetterFile(file);
            } else {
                toast.error("Please select a PDF file");
            }
        }
    };

    const uploadOccupancyLetter = async (event) => {
        event.stopPropagation();
        if (!occupancyLetterFile) {
            toast.error("No occupancy letter file selected.");
            return;
        }
        setUploadLoading(true);
        const uploadFormData = new FormData();
        uploadFormData.append("project_phase_id", phaseId);
        uploadFormData.append("media", occupancyLetterFile);

        try {
            await axiosInstance.post("/projects/phase/occupancy-letter", uploadFormData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            toast.success("Occupancy letter uploaded successfully!");
            setOccupancyLetterFile(null);
            // Clear the file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            fetchOccupancyLetters();
            // fetch data again to show the updated list
        } catch (error) {
            console.error("Error uploading occupancy letter:", error);
            toast.error("Error uploading occupancy letter.");
        } finally {
            setUploadLoading(false);
        }
    };

    const removeSelectedFile = () => {
        setOccupancyLetterFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const fetchOccupancyLetters = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get("/projects/phase/occupancy-letters", {
                params: {
                    project_phase_id: phaseId,
                },
            });
       
            setOccupancyLetters(response);
        } catch (error) {
            console.error("Error fetching occupancy documents:", error);
        } finally {
            setLoading(false);
        }
    };

    const downloadFile = async (selectedFile) => {
        if (!selectedFile || !selectedFile.content) {
            return;
        }

        try {
            const byteCharacters = atob(selectedFile.content);
            const byteArrays = [];

            for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
                const slice = byteCharacters.slice(offset, offset + 1024);
                const byteNumbers = new Array(slice.length);

                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }
            const blob = new Blob(byteArrays, { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;

            // Use the original file name without appending any extension.
            a.download = selectedFile.key;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading file:", error);
        }
    };

    useEffect(() => {
        if (projectId) {
            fetchOccupancyLetters();
        }
    }, [projectId]);

    return (
        <div className="space-y-6 p-4 ">
            <div className="flex items-center justify-between">
                <h1 className="text-lg font-semibold text-gray-900">Occupancy Letter Upload</h1>
            </div>

            {/* File Upload Area */}
            <div className="mb-6">
                <div className="relative">
                    <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleOccupancyLetterUpload}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        accept=".pdf"
                    />
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 lg:p-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                        <div className="flex flex-col items-center">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                                <UploadCloud className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                            </div>
                            <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                                Drop occupancy letter here or click to browse
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-500">
                                Supports PDF files only
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Selected File Preview */}
            {occupancyLetterFile && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-3 mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">{occupancyLetterFile.name}</p>
                            <p className="text-sm text-gray-500">
                                {(occupancyLetterFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                        </div>
                        <button
                            onClick={removeSelectedFile}
                            className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                            title="Remove file"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="flex justify-end">
                        <PrimaryButton
                            onClick={uploadOccupancyLetter}
                            disabled={uploadLoading}
                            className="bg-blue-600 hover:bg-blue-700 px-6 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                        >
                            {uploadLoading ? (
                                <div className="flex items-center space-x-2">
                                    <Spinner className="w-4 h-4" />
                                    <span>Uploading...</span>
                                </div>
                            ) : (
                                "Upload File"
                            )}
                        </PrimaryButton>
                    </div>
                </div>
            )}

            {/* Files List */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-gray-900">
                        Uploaded Occupancy Letters
                    </h4>
                    {loading && (
                        <div className="text-sm text-gray-500">
                            Loading...
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-40">
                        <Spinner color="gray" />
                    </div>
                ) : occupancyLetters && occupancyLetters.length > 0 ? (
                    <div className="space-y-3">
                        {occupancyLetters.map((file, index) => (
                            <div
                                key={file.key || index}
                                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow duration-200"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                                            <FaFilePdf className="text-orange-600 text-xl" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">
                                                {file.key || "OCCUPANCY_LETTER"}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                Click to download
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => downloadFile(file)}
                                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                        title="Download file"
                                    >
                                        <Download className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <div className="text-sm text-gray-500">
                            No occupancy letters uploaded yet
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OccupancyLetterUpload;